import { useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Paperclip, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useReply, useUploadAttachment, extractErrorMessage } from '@/queries/ticketMutations'
import { useAnswerAgentItem } from '@/queries/agent'
import { loadDraft, clearDraft, useDraftPersist } from '@/hooks/useDraft'
import { isAdminViewer } from '@/lib/scope'
import type { TicketStatus, ViewerRelation } from '@/types/ticket'
import { toast } from 'sonner'

const MAX_SIZE = 25 * 1024 * 1024

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  ticketId: string
  status: TicketStatus
  viewer: ViewerRelation
  initialBody?: string
  // Names where a seeded draft came from; it is only ever a starting point, sent by the person
  draftLabel?: string
  // Manual edit of an agent's draft: Send tells Agent Relay this staff member sent their version, which is then posted
  // as them (and only as them), instead of posting a reply here.
  // The draft being edited: its question, and its disclosure line as the latest poll has it. The line goes out after the
  // edit, so the composer shows it, and Send carries exactly the line on screen when it is pressed.
  editDraft?: { questionId: string; disclosure: string | null; onDone: () => void }
}

export function ReplyComposer({ ticketId, status, viewer, initialBody, draftLabel, editDraft }: Props) {
  const { user } = useAuth0()
  const reply = useReply(ticketId, { name: user?.name, isAdmin: isAdminViewer(viewer) })
  const answer = useAnswerAgentItem(ticketId)
  const upload = useUploadAttachment(ticketId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Draft survives navigation/refresh; cleared on successful send. An
  // investigation's "Use as reply" seed (via the composer's key remount)
  // takes priority over a stale localStorage draft.
  const [body, setBody] = useState(() => initialBody ?? loadDraft(ticketId))
  const [isInternal, setIsInternal] = useState(false)
  useDraftPersist(ticketId, body)
  const [attachments, setAttachments] = useState<Array<{ s3Key: string; filename: string; mimeType: string; size: number }>>([])
  const closed = status === 'closed'
  const isAdmin = isAdminViewer(viewer)
  // The line when the edit opened (Edit remounts the composer), so a line that changes while it is open is pointed out.
  const [openedWithDisclosure] = useState(() => editDraft?.disclosure ?? null)
  const disclosure = editDraft?.disclosure ?? null
  const disclosureChanged = Boolean(editDraft) && disclosure !== openedWithDisclosure

  async function handleFile(file: File | null | undefined) {
    if (!file) return
    if (file.size > MAX_SIZE) {
      toast.error(`${file.name} is over 25MB.`)
      return
    }
    try {
      const meta = await upload.mutateAsync(file)
      setAttachments(prev => [...prev, meta])
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Upload failed'))
    }
  }

  return (
    <form
      onSubmit={async e => {
        e.preventDefault()
        if (!body.trim()) return
        if (editDraft) {
          try {
            await answer.mutateAsync({
              questionId: editDraft.questionId,
              action: 'edit',
              text: body.trim(),
              // The line rendered below the editor in this render: the one the person saw when they pressed Send.
              shownDisclosure: disclosure
            })
            setBody('')
            clearDraft(ticketId)
            editDraft.onDone()
          } catch (err) {
            toast.error(extractErrorMessage(err, 'The edited reply did not reach the agent'))
          }
          return
        }
        try {
          await reply.mutateAsync({ body, attachments, isInternal: isAdmin && isInternal })
          setBody(''); setAttachments([]); setIsInternal(false)
          clearDraft(ticketId)
        } catch (err) {
          toast.error(extractErrorMessage(err, "Your reply didn't send. Please try again."))
        }
      }}
      className={`p-4 border-t space-y-2 ${isAdmin && isInternal ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}
      aria-label="Reply"
    >
      {closed && (
        <p className="text-etn-micro text-etn-text-soft">
          {isAdmin ? "This ticket is closed. A reply reopens it; an internal note doesn't." : 'This ticket is closed. Replying will reopen it.'}
        </p>
      )}
      {editDraft && (
        <div className="flex items-center gap-2" data-testid="editing-agent-draft">
          <span className="inline-flex rounded-etn-pill bg-etn-info-subtle px-2.5 py-0.5 text-etn-micro font-semibold text-etn-info-text">
            Editing the agent&apos;s draft: Send posts your version as you
          </span>
          <Button type="button" size="xs" variant="ghost" onClick={editDraft.onDone}>Cancel edit</Button>
        </div>
      )}
      {draftLabel && body.trim() && (
        <span className="inline-flex rounded-etn-pill bg-etn-accent-subtle px-2.5 py-0.5 text-etn-micro font-semibold text-etn-accent-text-active" data-testid="draft-label">
          {draftLabel}
        </span>
      )}
      <Textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Type your reply…" />
      {editDraft && disclosure && (
        <p className="border-t border-border pt-2 text-xs italic text-etn-text-soft" data-testid="edit-disclosure">
          <span className="not-italic font-semibold">Added after your text: </span>
          {disclosure}
        </p>
      )}
      {disclosureChanged && (
        <p className="text-xs font-semibold text-etn-warning-text" data-testid="edit-disclosure-changed">
          {disclosure
            ? 'The AI line changed while you were editing: check it above before you send.'
            : 'The AI line was removed while you were editing: nothing is added after your text.'}
        </p>
      )}

      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <li
              key={a.s3Key}
              className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/50 px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 text-muted-foreground" />
              <span className="max-w-[180px] truncate">{a.filename}</span>
              <span className="text-muted-foreground">{formatSize(a.size)}</span>
              <button
                type="button"
                aria-label={`Remove ${a.filename}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        aria-label="Attach file"
        onChange={e => {
          handleFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      <div className="flex items-center gap-3">
        {!editDraft && <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Paperclip className="h-3.5 w-3.5" />
          )}
          Attach
        </Button>}
        {isAdmin && !editDraft && (
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
            Internal note (hidden from customer)
          </label>
        )}
        <Button type="submit" disabled={reply.isPending || answer.isPending || !body.trim()} className="ml-auto">
          {reply.isPending || answer.isPending ? 'Sending…' : editDraft ? 'Send as me' : 'Send'}
        </Button>
      </div>
    </form>
  )
}
